import posthog from "posthog-js";

let initialized = false;

const HARDCODED_POSTHOG_KEY = "phc_u2LCBNfQJEVW8mi2YPqxYEhv6K5MzPoyaQwGHwA6yxmg";

const FIRST_TOUCH_KEYS = [
  "ft_referrer",
  "ft_landing_page",
  "ft_ts",
  "ft_utm_source",
  "ft_utm_medium",
  "ft_utm_campaign",
  "ft_utm_content",
  "ft_utm_term",
  "ft_fbclid",
] as const;

const ATTRIBUTION_QUERY_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
] as const;

function inferFirstTouchFromInitialPersonInfo(parsed: Record<string, unknown>): Record<string, unknown> {
  const initialPersonInfo = parsed.$initial_person_info;
  if (!initialPersonInfo || typeof initialPersonInfo !== "object") return {};

  const { r: referrer, u: initialUrl } = initialPersonInfo as Record<string, unknown>;
  if (typeof initialUrl !== "string") return {};

  try {
    const url = new URL(initialUrl);
    if (url.protocol !== "http:" && url.protocol !== "https:") return {};

    const firstTouch: Record<string, unknown> = {
      ft_referrer: typeof referrer === "string" && referrer !== "$direct" ? referrer : "direto",
      ft_landing_page: `${url.origin}${url.pathname}`,
    };

    for (const property of ATTRIBUTION_QUERY_KEYS) {
      firstTouch[`ft_${property}`] = url.searchParams.get(property) ?? "";
    }

    return firstTouch;
  } catch {
    return {};
  }
}

function alignLocalPersistenceWithCrossSubdomainCookie(key: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const persistenceKey = `ph_${key}_posthog`;
  try {
    const currentHostname = window.location?.hostname;
    const localValue = window.localStorage?.getItem(persistenceKey);
    if (!currentHostname || !localValue) return;

    const localPersistence = JSON.parse(localValue) as Record<string, unknown>;
    const candidates = document.cookie
      .split(";")
      .map(cookie => cookie.trim())
      .filter(cookie => cookie.startsWith(`${persistenceKey}=`));

    for (const candidate of candidates) {
      try {
        const decodedValue = decodeURIComponent(candidate.slice(persistenceKey.length + 1));
        const sharedPersistence = JSON.parse(decodedValue) as Record<string, unknown>;
        const initialPersonInfo = sharedPersistence.$initial_person_info;
        if (!initialPersonInfo || typeof initialPersonInfo !== "object") continue;

        const initialUrl = (initialPersonInfo as Record<string, unknown>).u;
        if (typeof initialUrl !== "string") continue;

        const initialHostname = new URL(initialUrl).hostname;
        if (
          initialHostname === currentHostname ||
          typeof sharedPersistence.distinct_id !== "string" ||
          sharedPersistence.distinct_id === localPersistence.distinct_id
        ) {
          continue;
        }

        window.localStorage.setItem(persistenceKey, decodedValue);
        return;
      } catch {
        continue;
      }
    }
  } catch {
    // Persistência indisponível ou inválida não deve impedir o carregamento do app.
  }
}

function readCrossSubdomainFirstTouch(key: string): Record<string, unknown> {
  if (typeof document === "undefined") return {};

  const cookiePrefix = `ph_${key}_posthog=`;
  try {
    const candidates = document.cookie
      .split(";")
      .map(cookie => cookie.trim())
      .filter(cookie => cookie.startsWith(cookiePrefix));

    for (const candidate of candidates) {
      try {
        const rawValue = candidate.slice(cookiePrefix.length);
        const parsed = JSON.parse(decodeURIComponent(rawValue)) as Record<string, unknown>;
        const firstTouch = Object.fromEntries(
          FIRST_TOUCH_KEYS
            .filter(property => Object.prototype.hasOwnProperty.call(parsed, property))
            .map(property => [property, parsed[property]]),
        );
        const inferredFirstTouch = inferFirstTouchFromInitialPersonInfo(parsed);
        const restoredFirstTouch = { ...inferredFirstTouch, ...firstTouch };
        if (Object.keys(restoredFirstTouch).length > 0) return restoredFirstTouch;
      } catch {
        continue;
      }
    }
  } catch {
    return {};
  }

  return {};
}

export function initPostHog(): typeof posthog | null {
  if (typeof window === "undefined") {
    return null;
  }

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY || HARDCODED_POSTHOG_KEY;
  const host = "/ingest";

  if (!key) {
    return null;
  }

  if (initialized) {
    return posthog;
  }

  alignLocalPersistenceWithCrossSubdomainCookie(key);
  const crossSubdomainFirstTouch = readCrossSubdomainFirstTouch(key);

  posthog.init(key, {
    api_host: host,
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    person_profiles: "identified_only",
    cross_subdomain_cookie: true,
    persistence: "localStorage+cookie",
    capture_pageview: false,
    loaded: ph => {
      if (process.env.NODE_ENV === "development") {
        ph.debug(false);
      }
    },
    __preview_measure_url: undefined,
  });

  if (Object.keys(crossSubdomainFirstTouch).length > 0) {
    posthog.register_once(crossSubdomainFirstTouch);
  }

  initialized = true;
  return posthog;
}

export function isPostHogInitialized(): boolean {
  return initialized && typeof window !== "undefined";
}

export function identifyFromToken(token: string | null | undefined): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  return import("@/lib/posthog-enrichment").then(mod => {
    mod.identifyAndEnrichFromToken(token);
  });
}

export function resetPostHog(): void {
  if (typeof window === "undefined" || !initialized) {
    return;
  }
  posthog.reset();
  void import("@/lib/posthog-enrichment").then(mod => {
    mod.clearPostHogEnrichmentCache();
  });
}

export { posthog };
